import { useMemo } from 'react';
import { Filters, GuestRating } from '@apps/accommodations/AccommodationsConsole.types';
import { useAccommodationsConsoleSearchParamsHelper } from '../useAccommodationsConsoleSearchParamsHelper';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useLocation } from '@react-router';
import { useUpdateAccommodationsConsoleSearchParams } from './useUpdateAccommodationsConsoleSearchParams';
import { FILTER_QUERY_PARAM_KEYS, DEFAULT_FILTERS } from './constants';

export type UseFilterQueryParamsReturn = ReturnType<typeof useFilterQueryParams>;

const stringValueToInt = (value: string) => parseInt(value, 10);

export const useFilterQueryParams = () => {
  const location = useLocation();
  const queryParamHelper = useAccommodationsConsoleSearchParamsHelper();
  const { applyAccommodationConsoleSearchParamsToUrl } = useUpdateAccommodationsConsoleSearchParams();
  const currentFilters = useMemo<Filters>(() => {
    const newFilters: UnsealedReadonly<Filters> = { ...DEFAULT_FILTERS };

    const facilitiesStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.FACILITIES);
    if (facilitiesStr) newFilters.facilities = facilitiesStr.split(',');

    const cugStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.CUG);
    if (cugStr) newFilters.cug = cugStr === 'true';

    const starRatingStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.STAR_RATING);
    if (starRatingStr) newFilters.starRating = starRatingStr.split(',').map(stringValueToInt);

    const userRatingStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.USER_RATING);
    if (userRatingStr) newFilters.userRating = userRatingStr.split(',').map(stringValueToInt) as GuestRating[];

    const priceMin = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PRICE_MIN);
    const priceMax = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.PRICE_MAX);
    if (priceMin && priceMax) {
      newFilters.priceRange = [stringValueToInt(priceMin), stringValueToInt(priceMax)];
    }

    const showAvailableOnlyStr = queryParamHelper.getValueString(FILTER_QUERY_PARAM_KEYS.SHOW_AVAILABLE_ONLY);
    if (showAvailableOnlyStr) newFilters.showAvailableOnly = showAvailableOnlyStr === 'true';

    return newFilters;
  }, [queryParamHelper]);

  const updateFilters = useEventCallback((newFilters: Filters, actionType: 'push' | 'replace' = 'push') => {
    const searchParams: Record<string, string | null> = {};
    // Update facilities
    searchParams[FILTER_QUERY_PARAM_KEYS.FACILITIES] = newFilters.facilities?.length ? newFilters.facilities.join(',') : null;

    // Update CUG
    searchParams[FILTER_QUERY_PARAM_KEYS.CUG] = newFilters.cug ? 'true' : null;

    // Update star rating
    searchParams[FILTER_QUERY_PARAM_KEYS.STAR_RATING] = newFilters.starRating?.length ? newFilters.starRating.join(',') : null;

    // Update user rating
    searchParams[FILTER_QUERY_PARAM_KEYS.USER_RATING] = newFilters.userRating?.length ? newFilters.userRating.join(',') : null;

    // Update price range
    if (newFilters.priceRange) {
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MIN] = newFilters.priceRange[0].toString();
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MAX] = newFilters.priceRange[1].toString();
    } else {
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MIN] = null;
      searchParams[FILTER_QUERY_PARAM_KEYS.PRICE_MAX] = null;
    }

    // Update show available only
    searchParams[FILTER_QUERY_PARAM_KEYS.SHOW_AVAILABLE_ONLY] = newFilters.showAvailableOnly ? 'true' : null;

    applyAccommodationConsoleSearchParamsToUrl({ searchParams, actionType, locationHash: location.hash });
  });

  const resetFilters = useEventCallback(() => {
    updateFilters(DEFAULT_FILTERS);
  });

  return {
    currentFilters,
    updateFilters,
    resetFilters,
  };
};
